/**
 * Firebase initialisation.
 *
 * The SDK configuration is NOT committed and NOT templated from a project ID.
 * D50 is absolute about that: the three project IDs are permanent, inconsistent
 * in shape, and a naive `cairn-${env}` would have silently worked for exactly
 * one of them. So the app asks the host which project it belongs to.
 *
 * Firebase Hosting serves `/__/firebase/init.json` on every site and every
 * preview channel, describing the project that site belongs to. One build
 * therefore runs correctly on cairn-develop, on a preview channel and on
 * cairn-live without a build-time variable, a per-environment bundle, or a
 * project ID anywhere in the source.
 *
 * Locally there is no Hosting emulator in `npm run dev`, so dev uses a stub
 * config and connects to the Auth and Firestore emulators. The values below
 * are deliberately not real: the emulators never check them.
 */

import {
  getApp,
  getApps,
  initializeApp,
  type FirebaseApp,
  type FirebaseOptions,
} from 'firebase/app';
import {
  browserLocalPersistence,
  connectAuthEmulator,
  getAuth,
  initializeAuth,
  type Auth,
} from 'firebase/auth';
import {
  connectFirestoreEmulator,
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
} from 'firebase/firestore';

/** Only ever used against the emulators, which do not validate any of it. */
const EMULATOR_CONFIG: FirebaseOptions = {
  apiKey: 'emulator-not-a-real-key',
  authDomain: 'localhost',
  projectId: 'cairn-develop',
  appId: '1:0:web:0',
};

const AUTH_EMULATOR = 'http://127.0.0.1:9099';
const FIRESTORE_EMULATOR_HOST = '127.0.0.1';
const FIRESTORE_EMULATOR_PORT = 8080;

interface Services {
  readonly app: FirebaseApp;
  readonly auth: Auth;
  readonly db: Firestore;
}

async function loadConfig(): Promise<FirebaseOptions> {
  if (import.meta.env.DEV) return EMULATOR_CONFIG;

  const response = await fetch('/__/firebase/init.json');
  if (!response.ok) {
    // Serving the built app from anything other than Firebase Hosting is the
    // usual cause. Fail loudly: a half-initialised SDK produces errors that
    // point everywhere except here.
    throw new Error(
      `Could not read /__/firebase/init.json (${String(response.status)}). ` +
        'The app must be served by Firebase Hosting.',
    );
  }
  return (await response.json()) as FirebaseOptions;
}

let services: Promise<Services> | null = null;

/**
 * `initializeAuth` and `initializeFirestore` are one-shot per app: calling
 * either twice throws, even with identical options. The memo below normally
 * prevents that, but Vite's HMR re-executes this module while the Firebase app
 * itself survives on `globalThis` — so on the second execution the memo is
 * empty and the SDK is already configured. Falling back to the getters is what
 * keeps a hot reload from wedging the whole app until a manual refresh.
 */
function configure(options: FirebaseOptions): Services {
  const app = getApps().length > 0 ? getApp() : initializeApp(options);

  let auth: Auth;
  try {
    // Local persistence, not session: a crew member's sign-in has to survive
    // closing the browser, the phone sleeping, and seven days of an event.
    // Firebase refresh tokens do not expire on their own, so this is what
    // makes the seven-day requirement hold.
    auth = initializeAuth(app, { persistence: browserLocalPersistence });
  } catch {
    auth = getAuth(app);
  }

  let db: Firestore;
  try {
    // The offline cache is the resilient-online posture: writes queue locally
    // and replay on reconnect. Multi-tab because HQ will have several open.
    db = initializeFirestore(app, {
      localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
    });
  } catch {
    db = getFirestore(app);
  }

  return { app, auth, db };
}

/**
 * Idempotent. Every repository function awaits this rather than assuming a
 * module-level singleton, because the configuration arrives over the network
 * in production.
 */
export function getServices(): Promise<Services> {
  services ??= (async (): Promise<Services> => {
    const created = configure(await loadConfig());

    if (import.meta.env.DEV) {
      // Both are no-ops if this module has already been through HMR and the
      // emulator connection was made on a previous execution.
      try {
        connectAuthEmulator(created.auth, AUTH_EMULATOR, { disableWarnings: true });
        connectFirestoreEmulator(created.db, FIRESTORE_EMULATOR_HOST, FIRESTORE_EMULATOR_PORT);
      } catch {
        /* Already connected. */
      }
    }

    return created;
  })();

  return services;
}
